import { Calendar, TeamMember, TeamRole, TodoTasksBoard } from '@demind-inc/core';
import { FC, FormEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  BoltRounded,
  CalendarMonthRounded,
  DeleteOutlineRounded,
  DriveFileRenameOutlineRounded,
  GroupsRounded,
  KeyboardArrowDownRounded,
  TaskAltRounded,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Popover,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import './TeamSharingSettings.scss';
import { AppSwitch, BasicDialog } from '../../common';
import { CalendarListItem } from '../../SideBar/CalendarsList/CalendarListItem';
import { SIDEBAR_ICONS } from '../../SideBar/constants';
import { useAuthContext, useCalendarsMeta, useTodoProjects } from '../../../data-access';
import { TEAM_NAME_MAX_LENGTH, TEAM_SETTINGS_SECTION_ID_MAP } from '../constants';
import { getTeamCurrentUserLabel, getTeamMemberFallbackName, TeamTFunction } from '../i18n';
import TeamProjectsSettings from '../TeamProjectsSettings';
import TeamRoleChip from '../TeamRoleChip';
import AppleCalendarIcon from '../../../assets/calendar/apple.png';
import GoogleCalendarIcon from '../../../assets/calendar/google.png';
import OutlookCalendarIcon from '../../../assets/calendar/outlook.png';
import { ReactComponent as LifestackCalendarIcon } from '../../../assets/icons/calendar-icon.svg';

interface TeamSharingSettingsValues {
  sharedCalendarIds: string[];
  sharedTaskBoardIds: string[];
  shareEnergy: boolean;
}

interface TeamSharingSettingsProps {
  teamId: string;
  teamName: string;
  teamRole: TeamRole;
  teamOwnerUserId: string;
  members: TeamMember[];
  boards: TodoTasksBoard[];
  sharedCalendarIds: string[];
  sharedTaskBoardIds: string[];
  shareEnergy: boolean;
  isSavingShareSettings: boolean;
  shareSettingsError: string | null;
  isRenamingTeam: boolean;
  renameTeamError: string | null;
  isUpdatingMemberRole: boolean;
  isRemovingMember: boolean;
  isTransferringOwnership: boolean;
  memberActionError: string | null;
  isCreatingTeamBoard: boolean;
  isUpdatingTeamBoard: boolean;
  isDeletingTeamBoard: boolean;
  teamProjectActionError: string | null;
  isLeavingTeam: boolean;
  leaveTeamError: string | null;
  onSaveShareSettings: (nextValues: TeamSharingSettingsValues) => Promise<void> | void;
  onRenameTeam: (nextTeamName: string) => Promise<boolean> | boolean;
  onUpdateMemberRole: (
    memberUserId: string,
    role: 'admin' | 'member'
  ) => Promise<boolean> | boolean;
  onRemoveMember: (memberUserId: string) => Promise<boolean> | boolean;
  onTransferTeamOwnership: (newOwnerUserId: string) => Promise<boolean> | boolean;
  onCreateTeamBoard: (boardName: string) => Promise<boolean> | boolean;
  onUpdateTeamBoard: (boardId: string, boardName: string) => Promise<boolean> | boolean;
  onDeleteTeamBoard: (boardId: string) => Promise<boolean> | boolean;
  onLeaveTeam: () => Promise<boolean> | boolean;
  onDeleteTeam: () => Promise<boolean> | boolean;
  onOpenInviteModal: () => void;
}

interface CalendarOption {
  id: string;
  name: string;
  provider: string;
  color: string;
  groupKey: string;
  groupLabel: string;
  calendar: Calendar;
}

interface TaskBoardOption {
  id: string;
  name: string;
  provider: string;
  groupKey: string;
  groupLabel: string;
}

interface SharingOptionGroup<T> {
  key: string;
  label: string;
  items: T[];
}

type TeamMemberWithMeta = TeamMember & {
  email?: string;
};

const SHARE_SETTINGS_SAVE_DEBOUNCE_MS = 350;

const normalizeIds = (ids: string[]) => {
  const seenMap: Record<string, boolean> = {};
  const uniqueIds: string[] = [];

  ids.forEach((id) => {
    if (!seenMap[id]) {
      seenMap[id] = true;
      uniqueIds.push(id);
    }
  });

  return uniqueIds.sort();
};

const buildSignature = (ids: string[]) => {
  return normalizeIds(ids).join('|');
};

const buildShareSettingsSignature = (values: TeamSharingSettingsValues) => {
  return [
    buildSignature(values.sharedCalendarIds),
    buildSignature(values.sharedTaskBoardIds),
    values.shareEnergy ? 'energy:on' : 'energy:off',
  ].join('::');
};

const mapOptionIds = (optionIds: string[]) => {
  return optionIds.reduce<Record<string, boolean>>((optionIdsMap, optionId) => {
    optionIdsMap[optionId] = true;
    return optionIdsMap;
  }, {});
};

const sanitizeSelection = (
  ids: string[],
  availableIdsMap: Record<string, boolean>,
  isOptionsLoading: boolean
) => {
  if (isOptionsLoading) {
    return normalizeIds(ids);
  }

  if (!Object.keys(availableIdsMap).length) {
    return [];
  }

  return normalizeIds(ids.filter((id) => Boolean(availableIdsMap[id])));
};

const getCalendarProviderLabel = (calendarType?: string, t?: TeamTFunction) => {
  if (calendarType === 'google') {
    return 'Google Calendar';
  }

  if (calendarType === 'outlook') {
    return 'Outlook Calendar';
  }

  if (calendarType === 'apple') {
    return 'Apple Calendar';
  }

  if (calendarType === 'lifestack') {
    return 'Lifestack Calendar';
  }

  return t ? t('team.common.calendar') : 'Calendar';
};

const getCalendarGroupLabel = (calendar: Calendar, t?: TeamTFunction) => {
  if (calendar.calendarType === 'lifestack') {
    return 'Lifestack';
  }

  return calendar.rootEmail?.trim() || getCalendarProviderLabel(calendar.calendarType, t);
};

const TeamCalendarProviderIcon: FC<{
  calendarType?: Calendar['calendarType'];
  className?: string;
}> = ({ calendarType, className }) => {
  if (calendarType === 'google') {
    return <img src={GoogleCalendarIcon} alt="" className={className} />;
  }

  if (calendarType === 'outlook') {
    return <img src={OutlookCalendarIcon} alt="" className={className} />;
  }

  if (calendarType === 'apple') {
    return <img src={AppleCalendarIcon} alt="" className={className} />;
  }

  if (calendarType === 'lifestack') {
    return <LifestackCalendarIcon className={className} />;
  }

  return null;
};

const getTaskProviderLabel = (appFrom?: string, t?: TeamTFunction) => {
  if (appFrom === 'todoist') {
    return 'Todoist';
  }

  if (appFrom === 'trello') {
    return 'Trello';
  }

  if (appFrom === 'ticktick') {
    return 'TickTick';
  }

  if (appFrom === 'clickup') {
    return 'ClickUp';
  }

  if (appFrom === 'github') {
    return 'GitHub';
  }

  if (appFrom === 'notion') {
    return 'Notion';
  }

  if (appFrom === 'googleTasks') {
    return 'Google Tasks';
  }

  if (appFrom === 'google-tasks') {
    return 'Google Tasks';
  }

  if (appFrom === 'apple-reminders') {
    return 'Apple Reminders';
  }

  if (appFrom === 'lifestack') {
    return 'Lifestack';
  }

  return t ? t('team.common.board') : 'Board';
};

const TODO_PROVIDER_ICON_KEYS = [
  'todoist',
  'trello',
  'ticktick',
  'clickup',
  'github',
  'google-tasks',
  'lifestack',
  'apple-reminders',
  'notion',
] as const;

type TodoProviderIconKey = (typeof TODO_PROVIDER_ICON_KEYS)[number];

const normalizeTaskProviderIconKey = (appFrom?: string): TodoProviderIconKey | null => {
  if (!appFrom) {
    return null;
  }

  const normalizedProvider =
    appFrom === 'googleTasks'
      ? 'google-tasks'
      : appFrom === 'appleReminders'
        ? 'apple-reminders'
        : appFrom;

  return TODO_PROVIDER_ICON_KEYS.includes(normalizedProvider as TodoProviderIconKey)
    ? (normalizedProvider as TodoProviderIconKey)
    : null;
};

const getTaskProviderIcon = (appFrom?: string) => {
  const iconKey = normalizeTaskProviderIconKey(appFrom);

  return iconKey ? SIDEBAR_ICONS[iconKey] : null;
};

const buildSharingOptionGroups = <T extends { groupKey: string; groupLabel: string }>(
  options: T[]
) => {
  const groupOrder: string[] = [];
  const groupsMap: Record<string, SharingOptionGroup<T>> = {};

  options.forEach((option) => {
    if (!groupsMap[option.groupKey]) {
      groupOrder.push(option.groupKey);
      groupsMap[option.groupKey] = {
        key: option.groupKey,
        label: option.groupLabel,
        items: [],
      };
    }

    groupsMap[option.groupKey].items.push(option);
  });

  return groupOrder.map((groupKey) => groupsMap[groupKey]);
};

const getMemberPrimaryLabel = (member: TeamMember, fallbackIndex: number, t?: TeamTFunction) => {
  const memberName = member.name?.trim();
  if (memberName) {
    return memberName;
  }

  const enrichedMember = member as TeamMemberWithMeta;
  const enrichedLabel = enrichedMember.email;

  if (enrichedLabel) {
    return enrichedLabel;
  }

  return t ? getTeamMemberFallbackName(t, fallbackIndex) : `Member ${fallbackIndex + 1}`;
};

const getMemberSecondaryLabel = (member: TeamMember, currentUserId: string) => {
  if (member.userId === currentUserId) {
    return '';
  }

  const enrichedMember = member as TeamMemberWithMeta;

  return enrichedMember.email || '';
};

const getAvatarInitials = (
  member: TeamMember,
  currentUserId: string,
  primaryLabel: string,
  secondaryLabel: string
) => {
  const memberName = member.name?.trim();

  if (memberName) {
    const nameTokens = memberName.split(/\s+/).filter(Boolean);

    if (nameTokens.length >= 2) {
      return `${nameTokens[0][0]}${nameTokens[1][0]}`.toUpperCase();
    }

    return memberName.slice(0, 2).toUpperCase();
  }

  if (member.userId === currentUserId) {
    return 'ME';
  }

  const sourceLabel = primaryLabel === 'You' ? secondaryLabel : primaryLabel;
  const normalizedLabel = sourceLabel.trim();

  if (!normalizedLabel) {
    return 'TM';
  }

  const labelTokens = normalizedLabel.split(/\s+/).filter(Boolean);

  if (labelTokens.length >= 2) {
    return `${labelTokens[0][0]}${labelTokens[1][0]}`.toUpperCase();
  }

  return normalizedLabel.slice(0, 2).toUpperCase();
};

export const TeamSharingSettings: FC<TeamSharingSettingsProps> = ({
  teamId,
  teamName,
  teamRole,
  teamOwnerUserId,
  members,
  boards,
  sharedCalendarIds,
  sharedTaskBoardIds,
  shareEnergy,
  isSavingShareSettings,
  shareSettingsError,
  isRenamingTeam,
  renameTeamError,
  isUpdatingMemberRole,
  isRemovingMember,
  isTransferringOwnership,
  memberActionError,
  isCreatingTeamBoard,
  isUpdatingTeamBoard,
  isDeletingTeamBoard,
  teamProjectActionError,
  isLeavingTeam,
  leaveTeamError,
  onSaveShareSettings,
  onRenameTeam,
  onUpdateMemberRole,
  onRemoveMember,
  onTransferTeamOwnership,
  onCreateTeamBoard,
  onUpdateTeamBoard,
  onDeleteTeamBoard,
  onLeaveTeam,
  onDeleteTeam,
  onOpenInviteModal,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const userId = user.userId || '';
  const { calendarsMeta, isLoading: isCalendarsLoading } = useCalendarsMeta({ userId });
  const { todoProjects, isLoading: isTaskBoardsLoading } = useTodoProjects({ userId });

  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>(sharedCalendarIds);
  const [selectedTaskBoardIds, setSelectedTaskBoardIds] = useState<string[]>(sharedTaskBoardIds);
  const [isEnergyShared, setIsEnergyShared] = useState<boolean>(shareEnergy);
  const [draftTeamName, setDraftTeamName] = useState(teamName);
  const [renameValidationError, setRenameValidationError] = useState<string | null>(null);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<TeamMember | null>(null);
  const [transferOwnershipTarget, setTransferOwnershipTarget] = useState<TeamMember | null>(null);
  const [roleActionsAnchorEl, setRoleActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [roleActionsMember, setRoleActionsMember] = useState<TeamMember | null>(null);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const selectedCalendarIdsRef = useRef(selectedCalendarIds);
  const selectedTaskBoardIdsRef = useRef(selectedTaskBoardIds);
  const isEnergySharedRef = useRef(isEnergyShared);
  const shareSaveTimeoutRef = useRef<number | null>(null);
  const isShareSaveInFlightRef = useRef(false);
  const queuedShareSettingsRef = useRef<TeamSharingSettingsValues | null>(null);
  const hasPendingLocalShareChangesRef = useRef(false);

  const calendarOptions = useMemo<CalendarOption[]>(() => {
    return calendarsMeta
      .filter((calendar) => Boolean(calendar.calendarId))
      .map((calendar) => {
        const groupLabel = getCalendarGroupLabel(calendar, t);

        return {
          id: calendar.calendarId,
          name: calendar.name?.trim() || t('team.common.untitledCalendar'),
          provider: getCalendarProviderLabel(calendar.calendarType, t),
          color: calendar.color || 'var(--color-primary, #680ddb)',
          groupKey: `${calendar.calendarType || 'calendar'}-${groupLabel}`,
          groupLabel,
          calendar,
        };
      });
  }, [calendarsMeta, t]);

  const taskBoardOptions = useMemo<TaskBoardOption[]>(() => {
    return todoProjects
      .filter((board) => Boolean(board.todoTaskId))
      .map((board) => {
        const provider = getTaskProviderLabel(board.appFrom, t);

        return {
          id: board.todoTaskId,
          name: board.name?.trim() || t('team.common.untitledBoard'),
          provider,
          groupKey: board.appFrom || provider,
          groupLabel: provider,
        };
      });
  }, [todoProjects, t]);

  const calendarOptionGroups = useMemo(
    () => buildSharingOptionGroups(calendarOptions),
    [calendarOptions]
  );
  const taskBoardOptionGroups = useMemo(
    () => buildSharingOptionGroups(taskBoardOptions),
    [taskBoardOptions]
  );

  const availableCalendarIdsMap = useMemo(() => {
    return mapOptionIds(calendarOptions.map((calendar) => calendar.id));
  }, [calendarOptions]);

  const availableTaskBoardIdsMap = useMemo(() => {
    return mapOptionIds(taskBoardOptions.map((taskBoard) => taskBoard.id));
  }, [taskBoardOptions]);

  const sanitizedSharedCalendarIds = useMemo(() => {
    return sanitizeSelection(sharedCalendarIds, availableCalendarIdsMap, isCalendarsLoading);
  }, [sharedCalendarIds, availableCalendarIdsMap, isCalendarsLoading]);

  const sanitizedSharedTaskBoardIds = useMemo(() => {
    return sanitizeSelection(sharedTaskBoardIds, availableTaskBoardIdsMap, isTaskBoardsLoading);
  }, [sharedTaskBoardIds, availableTaskBoardIdsMap, isTaskBoardsLoading]);

  const sharedCalendarSignature = useMemo(
    () => buildSignature(sanitizedSharedCalendarIds),
    [sanitizedSharedCalendarIds]
  );
  const sharedTaskBoardSignature = useMemo(
    () => buildSignature(sanitizedSharedTaskBoardIds),
    [sanitizedSharedTaskBoardIds]
  );
  const selectedCalendarSignature = useMemo(
    () => buildSignature(selectedCalendarIds),
    [selectedCalendarIds]
  );
  const selectedTaskBoardSignature = useMemo(
    () => buildSignature(selectedTaskBoardIds),
    [selectedTaskBoardIds]
  );
  const selectedShareSettingsSignature = useMemo(
    () =>
      buildShareSettingsSignature({
        sharedCalendarIds: selectedCalendarIds,
        sharedTaskBoardIds: selectedTaskBoardIds,
        shareEnergy: isEnergyShared,
      }),
    [isEnergyShared, selectedCalendarIds, selectedTaskBoardIds]
  );
  const serverShareSettingsSignature = useMemo(
    () =>
      buildShareSettingsSignature({
        sharedCalendarIds: sanitizedSharedCalendarIds,
        sharedTaskBoardIds: sanitizedSharedTaskBoardIds,
        shareEnergy,
      }),
    [sanitizedSharedCalendarIds, sanitizedSharedTaskBoardIds, shareEnergy]
  );

  useEffect(() => {
    selectedCalendarIdsRef.current = selectedCalendarIds;
  }, [selectedCalendarIds]);

  useEffect(() => {
    selectedTaskBoardIdsRef.current = selectedTaskBoardIds;
  }, [selectedTaskBoardIds]);

  useEffect(() => {
    isEnergySharedRef.current = isEnergyShared;
  }, [isEnergyShared]);

  useEffect(() => {
    hasPendingLocalShareChangesRef.current = false;

    if (shareSaveTimeoutRef.current) {
      window.clearTimeout(shareSaveTimeoutRef.current);
      shareSaveTimeoutRef.current = null;
    }

    queuedShareSettingsRef.current = null;
    isShareSaveInFlightRef.current = false;
    setRemoveMemberTarget(null);
    setTransferOwnershipTarget(null);
    setRoleActionsAnchorEl(null);
    setRoleActionsMember(null);
    setIsLeaveDialogOpen(false);
    setIsDeleteDialogOpen(false);
  }, [teamId]);

  useEffect(() => {
    if (hasPendingLocalShareChangesRef.current) {
      return;
    }

    setSelectedCalendarIds(sanitizedSharedCalendarIds);
    selectedCalendarIdsRef.current = sanitizedSharedCalendarIds;
    setSelectedTaskBoardIds(sanitizedSharedTaskBoardIds);
    selectedTaskBoardIdsRef.current = sanitizedSharedTaskBoardIds;
    setIsEnergyShared(shareEnergy);
    isEnergySharedRef.current = shareEnergy;
  }, [teamId, shareEnergy, sharedCalendarSignature, sharedTaskBoardSignature]);

  useEffect(() => {
    setDraftTeamName(teamName);
    setRenameValidationError(null);
  }, [teamId, teamName]);

  useEffect(() => {
    if (selectedShareSettingsSignature === serverShareSettingsSignature) {
      hasPendingLocalShareChangesRef.current = false;
    }
  }, [selectedShareSettingsSignature, serverShareSettingsSignature]);

  useEffect(() => {
    const normalizedCalendarIds = sanitizeSelection(
      selectedCalendarIds,
      availableCalendarIdsMap,
      isCalendarsLoading
    );
    const normalizedTaskBoardIds = sanitizeSelection(
      selectedTaskBoardIds,
      availableTaskBoardIdsMap,
      isTaskBoardsLoading
    );

    if (buildSignature(normalizedCalendarIds) !== selectedCalendarSignature) {
      setSelectedCalendarIds(normalizedCalendarIds);
      selectedCalendarIdsRef.current = normalizedCalendarIds;
    }

    if (buildSignature(normalizedTaskBoardIds) !== selectedTaskBoardSignature) {
      setSelectedTaskBoardIds(normalizedTaskBoardIds);
      selectedTaskBoardIdsRef.current = normalizedTaskBoardIds;
    }
  }, [
    availableCalendarIdsMap,
    availableTaskBoardIdsMap,
    isCalendarsLoading,
    isTaskBoardsLoading,
    selectedCalendarIds,
    selectedCalendarSignature,
    selectedTaskBoardIds,
    selectedTaskBoardSignature,
  ]);

  const activeMembers = useMemo(() => {
    return members
      .filter((member) => member.status === 'active')
      .sort((left, right) => {
        if (left.role === right.role) {
          return left.createdAt.localeCompare(right.createdAt);
        }

        const roleOrder: Record<TeamRole, number> = {
          owner: 0,
          admin: 1,
          member: 2,
        };

        return roleOrder[left.role] - roleOrder[right.role];
      });
  }, [members]);

  const isShareSectionDisabled = isCalendarsLoading || isTaskBoardsLoading;
  const isMemberActionBusy = isUpdatingMemberRole || isRemovingMember || isTransferringOwnership;
  const isOwner = teamRole === 'owner';
  const isAdmin = teamRole === 'admin';
  const canRenameTeam = isOwner || isAdmin;
  const canInviteMembers = isOwner || isAdmin;
  const canManageTeamProjects = isOwner || isAdmin;
  const otherActiveMembersCount = activeMembers.filter((member) => member.userId !== userId).length;
  const hasOtherActiveMembers = otherActiveMembersCount > 0;
  const shouldShowLeaveAction = !isOwner || hasOtherActiveMembers;
  const shouldShowTransferOwnershipAction = isOwner && hasOtherActiveMembers;
  const isLeaveActionDisabled = isLeavingTeam || isOwner;
  const isDeleteActionDisabled = isLeavingTeam;
  const leaveActionDescription = isOwner
    ? t('team.settings.delete.leaveOwnerDescription')
    : t('team.settings.delete.leaveDescription');
  const deleteActionDescription = t('team.settings.delete.deleteDescription');
  const renameError = renameValidationError || renameTeamError;
  const isRenameDisabled =
    isRenamingTeam ||
    draftTeamName.trim().length === 0 ||
    draftTeamName.trim().length > TEAM_NAME_MAX_LENGTH ||
    draftTeamName.trim() === teamName.trim();

  const normalizeShareSettingsValues = (nextValues: TeamSharingSettingsValues) => {
    return {
      sharedCalendarIds: sanitizeSelection(
        nextValues.sharedCalendarIds,
        availableCalendarIdsMap,
        isCalendarsLoading
      ),
      sharedTaskBoardIds: sanitizeSelection(
        nextValues.sharedTaskBoardIds,
        availableTaskBoardIdsMap,
        isTaskBoardsLoading
      ),
      shareEnergy: nextValues.shareEnergy,
    };
  };

  const persistShareSettings = async (nextValues: TeamSharingSettingsValues) => {
    const normalizedValues = normalizeShareSettingsValues(nextValues);

    if (isShareSaveInFlightRef.current) {
      queuedShareSettingsRef.current = normalizedValues;
      return;
    }

    isShareSaveInFlightRef.current = true;

    try {
      await onSaveShareSettings(normalizedValues);
    } finally {
      isShareSaveInFlightRef.current = false;

      const queuedValues = queuedShareSettingsRef.current;
      queuedShareSettingsRef.current = null;

      if (
        queuedValues &&
        buildShareSettingsSignature(queuedValues) !== buildShareSettingsSignature(normalizedValues)
      ) {
        void persistShareSettings(queuedValues);
      }
    }
  };

  const schedulePersistShareSettings = (nextValues: TeamSharingSettingsValues) => {
    const normalizedValues = normalizeShareSettingsValues(nextValues);
    const normalizedSignature = buildShareSettingsSignature(normalizedValues);

    if (shareSaveTimeoutRef.current) {
      window.clearTimeout(shareSaveTimeoutRef.current);
      shareSaveTimeoutRef.current = null;
    }

    if (!isShareSaveInFlightRef.current && normalizedSignature === serverShareSettingsSignature) {
      hasPendingLocalShareChangesRef.current = false;
      queuedShareSettingsRef.current = null;
      return;
    }

    hasPendingLocalShareChangesRef.current = true;
    shareSaveTimeoutRef.current = window.setTimeout(() => {
      shareSaveTimeoutRef.current = null;
      void persistShareSettings(normalizedValues);
    }, SHARE_SETTINGS_SAVE_DEBOUNCE_MS);
  };

  const setCalendarSelection = (calendarId: string, checked: boolean) => {
    if (isShareSectionDisabled) {
      return;
    }

    const currentCalendarIds = selectedCalendarIdsRef.current;
    const nextCalendarIds = checked
      ? currentCalendarIds.includes(calendarId)
        ? currentCalendarIds
        : [...currentCalendarIds, calendarId]
      : currentCalendarIds.filter((id) => id !== calendarId);

    selectedCalendarIdsRef.current = nextCalendarIds;
    setSelectedCalendarIds(nextCalendarIds);
    schedulePersistShareSettings({
      sharedCalendarIds: nextCalendarIds,
      sharedTaskBoardIds: selectedTaskBoardIdsRef.current,
      shareEnergy: isEnergySharedRef.current,
    });
  };

  const setTaskBoardSelection = (taskBoardId: string, checked: boolean) => {
    if (isShareSectionDisabled) {
      return;
    }

    const currentTaskBoardIds = selectedTaskBoardIdsRef.current;
    const nextTaskBoardIds = checked
      ? currentTaskBoardIds.includes(taskBoardId)
        ? currentTaskBoardIds
        : [...currentTaskBoardIds, taskBoardId]
      : currentTaskBoardIds.filter((id) => id !== taskBoardId);

    selectedTaskBoardIdsRef.current = nextTaskBoardIds;
    setSelectedTaskBoardIds(nextTaskBoardIds);
    schedulePersistShareSettings({
      sharedCalendarIds: selectedCalendarIdsRef.current,
      sharedTaskBoardIds: nextTaskBoardIds,
      shareEnergy: isEnergySharedRef.current,
    });
  };

  const toggleEnergySharing = () => {
    if (isShareSectionDisabled) {
      return;
    }

    const nextShareEnergy = !isEnergySharedRef.current;

    isEnergySharedRef.current = nextShareEnergy;
    setIsEnergyShared(nextShareEnergy);
    schedulePersistShareSettings({
      sharedCalendarIds: selectedCalendarIdsRef.current,
      sharedTaskBoardIds: selectedTaskBoardIdsRef.current,
      shareEnergy: nextShareEnergy,
    });
  };

  const handleRenameTeam = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedName = draftTeamName.trim();

    if (!normalizedName) {
      setRenameValidationError(t('team.settings.rename.validation.nameRequired'));
      return;
    }

    if (normalizedName.length > TEAM_NAME_MAX_LENGTH) {
      setRenameValidationError(
        t('team.settings.rename.validation.maxLength', { count: TEAM_NAME_MAX_LENGTH })
      );
      return;
    }

    setRenameValidationError(null);
    await onRenameTeam(normalizedName);
  };

  const handleToggleMemberRole = async (member: TeamMember) => {
    if (isMemberActionBusy || member.userId === userId || member.role === 'owner') {
      return;
    }

    const nextRole = member.role === 'admin' ? 'member' : 'admin';
    await onUpdateMemberRole(member.userId, nextRole);
  };

  const openRoleActionsMenu = (event: MouseEvent<HTMLButtonElement>, member: TeamMember) => {
    if (isMemberActionBusy) {
      return;
    }

    setRoleActionsAnchorEl(event.currentTarget);
    setRoleActionsMember(member);
  };

  const closeRoleActionsMenu = () => {
    setRoleActionsAnchorEl(null);
    setRoleActionsMember(null);
  };

  const handleRoleToggleFromMenu = async () => {
    if (!roleActionsMember) {
      return;
    }

    const targetMember = roleActionsMember;
    closeRoleActionsMenu();
    await handleToggleMemberRole(targetMember);
  };

  const handleTransferOwnershipFromMenu = () => {
    if (!roleActionsMember) {
      return;
    }

    const targetMember = roleActionsMember;
    closeRoleActionsMenu();
    setTransferOwnershipTarget(targetMember);
  };

  const handleConfirmRemoveMember = async () => {
    if (!removeMemberTarget) {
      return;
    }

    const didRemoveMember = await onRemoveMember(removeMemberTarget.userId);
    if (didRemoveMember) {
      setRemoveMemberTarget(null);
    }
  };

  const handleConfirmTransferOwnership = async () => {
    if (!transferOwnershipTarget) {
      return;
    }

    const didTransferOwnership = await onTransferTeamOwnership(transferOwnershipTarget.userId);
    if (didTransferOwnership) {
      setTransferOwnershipTarget(null);
    }
  };

  const handleConfirmLeaveTeam = async () => {
    const didLeaveTeam = await onLeaveTeam();
    if (didLeaveTeam) {
      setIsLeaveDialogOpen(false);
    }
  };

  const handleConfirmDeleteTeam = async () => {
    const didDeleteTeam = await onDeleteTeam();
    if (didDeleteTeam) {
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <main className="team-sharing-settings" aria-live="polite" aria-busy={isSavingShareSettings}>
      {canRenameTeam && (
        <section
          id={TEAM_SETTINGS_SECTION_ID_MAP.rename}
          className="team-sharing-settings__section team-sharing-settings__section--anchor"
        >
          <div className="team-sharing-settings__section-heading">
            <div className="team-sharing-settings__section-icon">
              <DriveFileRenameOutlineRounded />
            </div>
            <div className="team-sharing-settings__section-copy">
              <Typography
                variant="body1"
                component="h2"
                className="team-sharing-settings__section-title"
              >
                {t('team.settings.rename.title')}
              </Typography>
              <Typography
                variant="body2"
                component="p"
                className="team-sharing-settings__section-description"
              >
                {t('team.settings.rename.description')}
              </Typography>
            </div>
          </div>

          <form className="team-sharing-settings__rename-form" onSubmit={handleRenameTeam}>
            <label htmlFor="team-settings-name" className="team-sharing-settings__field-label">
              <Typography variant="body2" component="span">
                {t('team.common.teamName')}
              </Typography>
            </label>
            <input
              id="team-settings-name"
              className="team-sharing-settings__field"
              value={draftTeamName}
              onChange={(event) => {
                setDraftTeamName(event.target.value);
                if (renameValidationError) {
                  setRenameValidationError(null);
                }
              }}
              maxLength={TEAM_NAME_MAX_LENGTH}
              placeholder={t('team.common.teamNamePlaceholder')}
              disabled={isRenamingTeam}
            />
            <div className="team-sharing-settings__helper-row">
              <Typography
                variant="caption"
                component="span"
                className="team-sharing-settings__field-counter"
              >
                {draftTeamName.trim().length}/{TEAM_NAME_MAX_LENGTH}
              </Typography>
            </div>

            <div className="team-sharing-settings__actions">
              <Button
                type="submit"
                variant="contained"
                className="team-page__button team-page__button--primary team-sharing-settings__danger-row-action"
                disabled={isRenameDisabled}
              >
                <Typography variant="body2" component="span">
                  {isRenamingTeam ? t('team.common.updating') : t('team.settings.rename.title')}
                </Typography>
              </Button>
            </div>

            {renameError && (
              <Typography variant="body2" component="p" className="team-sharing-settings__error">
                {renameError}
              </Typography>
            )}
          </form>
        </section>
      )}

      <section
        id={TEAM_SETTINGS_SECTION_ID_MAP.sharedCalendar}
        className="team-sharing-settings__section team-sharing-settings__section--anchor"
      >
        <div className="team-sharing-settings__section-heading">
          <div className="team-sharing-settings__section-icon">
            <CalendarMonthRounded />
          </div>
          <div className="team-sharing-settings__section-copy">
            <Typography
              variant="body1"
              component="h2"
              className="team-sharing-settings__section-title"
            >
              {t('team.settings.sharedCalendar.title')}
            </Typography>
            <Typography
              variant="body2"
              component="p"
              className="team-sharing-settings__section-description"
            >
              {t('team.settings.sharedCalendar.description')}
            </Typography>
          </div>
        </div>

        <div className="team-sharing-settings__list team-sharing-settings__list--accordion">
          {isCalendarsLoading && (
            <Typography variant="body2" component="p" className="team-sharing-settings__list-empty">
              {t('team.settings.sharedCalendar.loading')}
            </Typography>
          )}

          {!isCalendarsLoading && !calendarOptions.length && (
            <Typography variant="body2" component="p" className="team-sharing-settings__list-empty">
              {t('team.common.noCalendarsFound')}
            </Typography>
          )}

          {!isCalendarsLoading &&
            calendarOptionGroups.map((group) => {
              const calendarType = group.items[0]?.calendar.calendarType;

              return (
                <Accordion
                  key={group.key}
                  className="team-sharing-settings__sharing-accordion"
                  elevation={0}
                  defaultExpanded
                >
                  <AccordionSummary
                    className="team-sharing-settings__sharing-accordion-summary"
                    expandIcon={
                      <KeyboardArrowDownRounded className="team-sharing-settings__sharing-accordion-chevron" />
                    }
                  >
                    <div className="team-sharing-settings__sharing-accordion-title">
                      <span className="team-sharing-settings__sharing-accordion-label">
                        <TeamCalendarProviderIcon
                          calendarType={calendarType}
                          className="team-sharing-settings__provider-icon"
                        />
                        <Typography
                          variant="body2"
                          component="span"
                          className="team-sharing-settings__sharing-accordion-name"
                        >
                          {group.label}
                        </Typography>
                      </span>
                      <Typography
                        variant="subtitle2"
                        component="span"
                        className="team-sharing-settings__sharing-accordion-count"
                      >
                        {t('team.settings.sharedCalendar.calendarCount', {
                          count: group.items.length,
                        })}
                      </Typography>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className="team-sharing-settings__sharing-accordion-details">
                    {group.items.map((calendar) => (
                      <CalendarListItem
                        key={calendar.id}
                        calendar={calendar.calendar}
                        selected={selectedCalendarIds.includes(calendar.id)}
                        onSelect={(checked) => setCalendarSelection(calendar.id, checked)}
                        className="team-sharing-settings__calendar-list-item"
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </div>
      </section>

      <section
        id={TEAM_SETTINGS_SECTION_ID_MAP.sharedTodos}
        className="team-sharing-settings__section team-sharing-settings__section--anchor"
      >
        <div className="team-sharing-settings__section-heading">
          <div className="team-sharing-settings__section-icon">
            <TaskAltRounded />
          </div>
          <div className="team-sharing-settings__section-copy">
            <Typography
              variant="body1"
              component="h2"
              className="team-sharing-settings__section-title"
            >
              {t('team.settings.sharedTodos.title')}
            </Typography>
            <Typography
              variant="body2"
              component="p"
              className="team-sharing-settings__section-description"
            >
              {t('team.settings.sharedTodos.description')}
            </Typography>
          </div>
        </div>

        <div className="team-sharing-settings__list team-sharing-settings__list--accordion">
          {isTaskBoardsLoading && (
            <Typography variant="body2" component="p" className="team-sharing-settings__list-empty">
              {t('team.settings.sharedTodos.loading')}
            </Typography>
          )}

          {!isTaskBoardsLoading && !taskBoardOptions.length && (
            <Typography variant="body2" component="p" className="team-sharing-settings__list-empty">
              {t('team.common.noTaskBoardsFound')}
            </Typography>
          )}

          {!isTaskBoardsLoading &&
            taskBoardOptionGroups.map((group) => {
              const providerIcon = getTaskProviderIcon(group.key);

              return (
                <Accordion
                  key={group.key}
                  className="team-sharing-settings__sharing-accordion"
                  elevation={0}
                  defaultExpanded
                >
                  <AccordionSummary
                    className="team-sharing-settings__sharing-accordion-summary"
                    expandIcon={
                      <KeyboardArrowDownRounded className="team-sharing-settings__sharing-accordion-chevron" />
                    }
                  >
                    <div className="team-sharing-settings__sharing-accordion-title">
                      <span className="team-sharing-settings__sharing-accordion-label">
                        {providerIcon && (
                          <img
                            src={providerIcon}
                            alt=""
                            aria-hidden="true"
                            className="team-sharing-settings__provider-icon"
                          />
                        )}
                        <Typography
                          variant="body2"
                          component="span"
                          className="team-sharing-settings__sharing-accordion-name"
                        >
                          {group.label}
                        </Typography>
                      </span>
                      <Typography
                        variant="subtitle2"
                        component="span"
                        className="team-sharing-settings__sharing-accordion-count"
                      >
                        {t('team.settings.sharedTodos.boardCount', {
                          count: group.items.length,
                        })}
                      </Typography>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className="team-sharing-settings__sharing-accordion-details">
                    {group.items.map((taskBoard) => (
                      <label key={taskBoard.id} className="team-sharing-settings__list-item">
                        <Checkbox
                          checked={selectedTaskBoardIds.includes(taskBoard.id)}
                          onChange={(event) =>
                            setTaskBoardSelection(taskBoard.id, event.target.checked)
                          }
                          className="team-sharing-settings__checkbox"
                          disabled={isShareSectionDisabled}
                        />
                        <div className="team-sharing-settings__list-copy">
                          <Typography
                            variant="body2"
                            component="span"
                            className="team-sharing-settings__list-title"
                          >
                            {taskBoard.name}
                          </Typography>
                        </div>
                      </label>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </div>
      </section>

      <section
        id={TEAM_SETTINGS_SECTION_ID_MAP.sharedEnergy}
        className="team-sharing-settings__section team-sharing-settings__section--anchor"
      >
        <div className="team-sharing-settings__section-heading">
          <div className="team-sharing-settings__section-icon">
            <BoltRounded />
          </div>
          <div className="team-sharing-settings__section-copy">
            <Typography
              variant="body1"
              component="h2"
              className="team-sharing-settings__section-title"
            >
              {t('team.settings.sharedEnergy.title')}
            </Typography>
            <Typography
              variant="body2"
              component="p"
              className="team-sharing-settings__section-description"
            >
              {t('team.settings.sharedEnergy.description')}
            </Typography>
          </div>
        </div>

        <div className="team-sharing-settings__toggle-row">
          <div className="team-sharing-settings__toggle-copy">
            <Typography
              variant="body2"
              component="span"
              className="team-sharing-settings__toggle-title"
            >
              {t('team.settings.sharedEnergy.toggleTitle')}
            </Typography>
            <Typography
              variant="subtitle2"
              component="span"
              className="team-sharing-settings__toggle-description"
            >
              {isEnergyShared
                ? t('team.settings.sharedEnergy.sharedDescription')
                : t('team.settings.sharedEnergy.privateDescription')}
            </Typography>
          </div>
          <AppSwitch
            checked={isEnergyShared}
            onChange={toggleEnergySharing}
            disabled={isShareSectionDisabled}
          />
        </div>

        {shareSettingsError && (
          <Typography variant="body2" component="p" className="team-sharing-settings__error">
            {shareSettingsError}
          </Typography>
        )}
      </section>

      {canManageTeamProjects && (
        <TeamProjectsSettings
          boards={boards}
          canManageProjects={canManageTeamProjects}
          isCreatingProject={isCreatingTeamBoard}
          isUpdatingProject={isUpdatingTeamBoard}
          isDeletingProject={isDeletingTeamBoard}
          projectActionError={teamProjectActionError}
          onCreateProject={onCreateTeamBoard}
          onRenameProject={onUpdateTeamBoard}
          onDeleteProject={onDeleteTeamBoard}
        />
      )}

      <section
        id={TEAM_SETTINGS_SECTION_ID_MAP.members}
        className="team-sharing-settings__section team-sharing-settings__section--anchor"
      >
        <div className="team-sharing-settings__section-heading team-sharing-settings__section-heading--members">
          <div className="team-sharing-settings__section-heading-main">
            <div className="team-sharing-settings__section-icon">
              <GroupsRounded />
            </div>
            <div className="team-sharing-settings__section-copy">
              <Typography
                variant="body1"
                component="h2"
                className="team-sharing-settings__section-title"
              >
                {t('team.settings.members.title')}
              </Typography>
              <Typography
                variant="body2"
                component="p"
                className="team-sharing-settings__section-description"
              >
                {t('team.settings.members.description')}
              </Typography>
            </div>
          </div>
          {canInviteMembers && (
            <Button
              type="button"
              variant="contained"
              className="team-page__button team-page__button--primary team-sharing-settings__members-invite-button"
              onClick={onOpenInviteModal}
            >
              <Typography variant="body2" component="span">
                {t('team.common.invite')}
              </Typography>
            </Button>
          )}
        </div>

        <div className="team-sharing-settings__members">
          {!activeMembers.length && (
            <Typography variant="body2" component="p" className="team-sharing-settings__list-empty">
              {t('team.settings.members.empty')}
            </Typography>
          )}

          {activeMembers.map((member, index) => {
            const primaryLabel = getMemberPrimaryLabel(member, index, t);
            const secondaryLabel = getMemberSecondaryLabel(member, userId);
            const displayNameLabel =
              member.userId === userId ? getTeamCurrentUserLabel(t, primaryLabel) : primaryLabel;
            const memberPhotoUrl = member.photoUrl?.trim() || '';
            const canToggleRole = isOwner && member.userId !== userId && member.role !== 'owner';
            const canTransferOwnership =
              shouldShowTransferOwnershipAction &&
              member.userId !== userId &&
              member.userId !== teamOwnerUserId;
            const canRemoveMember =
              member.userId !== userId &&
              ((isOwner && member.role !== 'owner') || (isAdmin && member.role === 'member'));
            const canChangeRole = canToggleRole || canTransferOwnership;

            return (
              <div
                key={member.teamMemberId || `${member.teamId}-${member.userId}`}
                className="team-sharing-settings__member-row"
              >
                <div className="team-sharing-settings__member-avatar">
                  {memberPhotoUrl ? (
                    <img
                      src={memberPhotoUrl}
                      alt={primaryLabel}
                      className="team-sharing-settings__member-avatar-image"
                    />
                  ) : (
                    getAvatarInitials(member, userId, primaryLabel, secondaryLabel)
                  )}
                </div>
                <div className="team-sharing-settings__member-copy">
                  <Typography
                    variant="body2"
                    component="span"
                    className="team-sharing-settings__member-name"
                  >
                    {displayNameLabel}
                  </Typography>
                  <TeamRoleChip
                    role={member.role}
                    className="team-sharing-settings__member-role-chip"
                  />
                  {secondaryLabel && (
                    <Typography
                      variant="subtitle2"
                      component="span"
                      className="team-sharing-settings__member-meta"
                    >
                      {secondaryLabel}
                    </Typography>
                  )}
                </div>
                {(canChangeRole || canRemoveMember) && (
                  <div className="team-sharing-settings__member-actions">
                    {canChangeRole && (
                      <Button
                        type="button"
                        variant="outlined"
                        className="team-page__button team-page__button--secondary team-sharing-settings__member-button team-sharing-settings__member-button--role"
                        disabled={isMemberActionBusy}
                        onClick={(event) => openRoleActionsMenu(event, member)}
                      >
                        <Typography
                          variant="body2"
                          component="span"
                          className="team-sharing-settings__member-button-label"
                        >
                          {t('team.settings.members.changeRole')}
                        </Typography>
                        <KeyboardArrowDownRounded className="team-sharing-settings__member-button-icon" />
                      </Button>
                    )}

                    {canRemoveMember && (
                      <Button
                        type="button"
                        variant="outlined"
                        className="team-page__button team-page__button--secondary team-sharing-settings__member-button team-sharing-settings__member-button--danger"
                        disabled={isMemberActionBusy}
                        onClick={() => setRemoveMemberTarget(member)}
                      >
                        <Typography variant="body2" component="span">
                          {t('team.common.remove')}
                        </Typography>
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Popover
          open={Boolean(roleActionsAnchorEl && roleActionsMember)}
          anchorEl={roleActionsAnchorEl}
          onClose={closeRoleActionsMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="team-sharing-settings__member-role-menu-popover"
        >
          <div className="team-sharing-settings__member-role-menu" role="menu">
            {roleActionsMember &&
              roleActionsMember.userId !== userId &&
              roleActionsMember.role !== 'owner' &&
              isOwner && (
                <button
                  type="button"
                  role="menuitem"
                  className="team-sharing-settings__member-role-menu-item"
                  disabled={isMemberActionBusy}
                  onClick={() => {
                    void handleRoleToggleFromMenu();
                  }}
                >
                  <Typography variant="body2" component="span">
                    {roleActionsMember.role === 'admin'
                      ? t('team.settings.members.makeMember')
                      : t('team.settings.members.makeAdmin')}
                  </Typography>
                </button>
              )}

            {roleActionsMember &&
              shouldShowTransferOwnershipAction &&
              roleActionsMember.userId !== userId &&
              roleActionsMember.userId !== teamOwnerUserId && (
                <button
                  type="button"
                  role="menuitem"
                  className="team-sharing-settings__member-role-menu-item"
                  disabled={isMemberActionBusy}
                  onClick={handleTransferOwnershipFromMenu}
                >
                  <Typography variant="body2" component="span">
                    {t('team.settings.members.makeOwner')}
                  </Typography>
                </button>
              )}
          </div>
        </Popover>

        {memberActionError && (
          <Typography variant="body2" component="p" className="team-sharing-settings__error">
            {memberActionError}
          </Typography>
        )}
      </section>

      <section
        id={TEAM_SETTINGS_SECTION_ID_MAP.delete}
        className="team-sharing-settings__section team-sharing-settings__section--anchor team-sharing-settings__section--danger"
      >
        <div className="team-sharing-settings__section-heading">
          <div className="team-sharing-settings__section-icon team-sharing-settings__section-icon--danger">
            <DeleteOutlineRounded />
          </div>
          <div className="team-sharing-settings__section-copy">
            <Typography
              variant="body1"
              component="h2"
              className="team-sharing-settings__section-title"
            >
              {t('team.settings.delete.title')}
            </Typography>
            <Typography
              variant="body2"
              component="p"
              className="team-sharing-settings__section-description"
            >
              {t('team.settings.delete.description')}
            </Typography>
          </div>
        </div>

        <div className="team-sharing-settings__danger-actions">
          {shouldShowLeaveAction && (
            <div
              className={`team-sharing-settings__danger-row${
                isOwner ? ' team-sharing-settings__danger-row--archive' : ''
              }`}
            >
              <div className="team-sharing-settings__danger-row-copy">
                <Typography
                  variant="body2"
                  component="p"
                  className="team-sharing-settings__danger-row-title"
                >
                  {t('team.settings.delete.leaveTeam')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  component="p"
                  className="team-sharing-settings__danger-row-description"
                >
                  {leaveActionDescription}
                </Typography>
              </div>
              <Button
                type="button"
                variant="outlined"
                className="team-page__button team-page__button--secondary team-sharing-settings__danger-row-action"
                disabled={isLeaveActionDisabled}
                onClick={() => setIsLeaveDialogOpen(true)}
              >
                <Typography variant="body2" component="span">
                  {t('team.settings.delete.leaveTeam')}
                </Typography>
              </Button>
            </div>
          )}

          {isOwner && (
            <div
              className={`team-sharing-settings__danger-row${
                shouldShowLeaveAction ? ' team-sharing-settings__danger-row--archive' : ''
              }`}
            >
              <div className="team-sharing-settings__danger-row-copy">
                <Typography
                  variant="body2"
                  component="p"
                  className="team-sharing-settings__danger-row-title team-sharing-settings__danger-row-title--danger"
                >
                  {t('team.settings.delete.deleteTeam')}
                </Typography>
                <Typography
                  variant="subtitle2"
                  component="p"
                  className="team-sharing-settings__danger-row-description"
                >
                  {deleteActionDescription}
                </Typography>
              </div>
              <Button
                type="button"
                variant="contained"
                className="team-page__button team-page__button--primary team-sharing-settings__danger-row-action team-sharing-settings__danger-button"
                disabled={isDeleteActionDisabled}
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Typography variant="body2" component="span">
                  {isLeavingTeam ? t('team.common.working') : t('team.settings.delete.deleteTeam')}
                </Typography>
              </Button>
            </div>
          )}

          {leaveTeamError && (
            <Typography variant="body2" component="p" className="team-sharing-settings__error">
              {leaveTeamError}
            </Typography>
          )}
        </div>
      </section>

      <BasicDialog
        visible={Boolean(removeMemberTarget)}
        title={t('team.settings.members.removeMemberTitle')}
        description={t('team.settings.members.removeMemberDescription', {
          name: removeMemberTarget
            ? getMemberPrimaryLabel(removeMemberTarget, 0, t)
            : t('team.settings.members.thisMember'),
        })}
        ctaString={t('team.common.remove')}
        isLoading={isRemovingMember}
        onYes={handleConfirmRemoveMember}
        onClose={() => {
          if (!isRemovingMember) {
            setRemoveMemberTarget(null);
          }
        }}
      />

      <BasicDialog
        visible={Boolean(transferOwnershipTarget)}
        title={t('team.settings.members.transferOwnershipTitle')}
        description={t('team.settings.members.transferOwnershipDescription', {
          name: transferOwnershipTarget
            ? getMemberPrimaryLabel(transferOwnershipTarget, 0, t)
            : t('team.settings.members.thisMember'),
        })}
        ctaString={t('team.settings.members.transfer')}
        isLoading={isTransferringOwnership}
        onYes={handleConfirmTransferOwnership}
        onClose={() => {
          if (!isTransferringOwnership) {
            setTransferOwnershipTarget(null);
          }
        }}
      />

      <BasicDialog
        visible={isLeaveDialogOpen}
        title={t('team.settings.delete.leaveTeam')}
        description={t('team.settings.delete.leaveConfirmDescription')}
        ctaString={t('team.settings.delete.leaveTeam')}
        isLoading={isLeavingTeam}
        onYes={handleConfirmLeaveTeam}
        onClose={() => {
          if (!isLeavingTeam) {
            setIsLeaveDialogOpen(false);
          }
        }}
      />

      <BasicDialog
        visible={isDeleteDialogOpen}
        title={t('team.settings.delete.deleteTeam')}
        description={t('team.settings.delete.deleteConfirmDescription')}
        ctaString={t('team.settings.delete.deleteTeam')}
        isLoading={isLeavingTeam}
        onYes={handleConfirmDeleteTeam}
        onClose={() => {
          if (!isLeavingTeam) {
            setIsDeleteDialogOpen(false);
          }
        }}
      />
    </main>
  );
};

export default TeamSharingSettings;
